import React, { useState, useEffect } from "react";
import { useHistory, NavLink } from "react-router-dom";
import {
  Box,
  Flex,
  Heading,
  Text,
  Input,
  InputGroup,
  InputRightElement,
  Button,
  IconButton,
  Image,
  Link,
  Spinner,
  useToast,
} from "@chakra-ui/react";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { supabase } from "lib/supabase";
import logoNotitia from "assets/img/logo-notitia.png";

// Page atteinte via le lien reçu par e-mail (resetPasswordForEmail). Supabase établit
// une session de récupération (detectSessionInUrl) ; on laisse alors définir un nouveau mot de passe.
export default function ResetPassword() {
  const history = useHistory();
  const toast = useToast();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [erreur, setErreur] = useState("");
  const [chargement, setChargement] = useState(false);
  const [pret, setPret] = useState(false); // session de récupération détectée
  const [verifie, setVerifie] = useState(false);

  useEffect(() => {
    let actif = true;
    supabase.auth.getSession().then(({ data }) => {
      if (!actif) return;
      if (data.session) setPret(true);
      setVerifie(true);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((event, sess) => {
      if (event === "PASSWORD_RECOVERY" || (event === "SIGNED_IN" && sess)) {
        setPret(true);
        setVerifie(true);
      }
    });
    return () => { actif = false; sub.subscription.unsubscribe(); };
  }, []);

  async function definir() {
    setErreur("");
    if (password.length < 8) { setErreur("Le mot de passe doit contenir au moins 8 caractères."); return; }
    if (password !== confirm) { setErreur("Les deux mots de passe ne correspondent pas."); return; }
    setChargement(true);
    const { error } = await supabase.auth.updateUser({ password });
    setChargement(false);
    if (error) { setErreur(error.message || "Impossible de mettre à jour le mot de passe."); return; }
    toast({ title: "Mot de passe mis à jour", description: "Vous pouvez maintenant vous connecter.", status: "success", duration: 3500 });
    await supabase.auth.signOut();
    history.push("/auth/signin");
  }

  const champStyle = {
    bg: "gray.50",
    border: "1px solid",
    borderColor: "gray.200",
    borderRadius: "12px",
    fontSize: "sm",
    h: "46px",
    _hover: { borderColor: "gray.300" },
    _focus: { borderColor: "teal.300", boxShadow: "0 0 0 1px #4FD1C5", bg: "white" },
  };
  const Label = ({ children }) => (
    <Flex align="center" mb="6px">
      <Box w="3px" h="14px" bg="teal.400" borderRadius="full" me="8px" flexShrink={0} />
      <Text fontSize="sm" fontWeight="bold" color="gray.700">{children}</Text>
    </Flex>
  );

  return (
    <Flex minH="100vh" align="center" justify="center" bgGradient="linear(135deg, #E5FBF7 0%, #A9EDE4 60%, #7FE3D6 100%)" p={{ base: "16px", md: "32px" }}>
      <Box position="relative" w="100%" maxW="1180px" borderRadius="34px" bg="whiteAlpha.400" backdropFilter="blur(6px)" boxShadow="0 30px 80px rgba(45,116,110,0.25)" p={{ base: "16px", md: "36px" }} overflow="hidden">
        <Flex direction={{ base: "column", lg: "row" }} align="center" justify="center" gap={{ base: "20px", lg: "10px" }}>
          <Box bg="white" borderRadius="24px" p={{ base: "28px", md: "40px" }} w={{ base: "100%", lg: "440px" }} flexShrink={0} boxShadow="0 20px 50px rgba(45,116,110,0.15)">
            <Flex align="center" mb="10px">
              <Box w="6px" h="38px" bg="teal.400" borderRadius="full" me="12px" flexShrink={0} />
              <Heading color="gray.800" fontSize="32px" fontWeight="bold" lineHeight="1.1">Nouveau mot de passe</Heading>
            </Flex>

            {!verifie ? (
              <Flex align="center" gap="10px" py="30px"><Spinner color="teal.300" /><Text fontSize="sm" color="gray.500">Vérification du lien…</Text></Flex>
            ) : !pret ? (
              <Box>
                <Text fontSize="sm" color="gray.600" mb="20px" lineHeight="1.6">
                  Ce lien de réinitialisation est invalide ou a expiré. Demandez-en un nouveau depuis la page de connexion.
                </Text>
                <Button w="100%" h="46px" bg="teal.400" color="white" borderRadius="12px" fontWeight="bold" _hover={{ bg: "teal.500" }} onClick={() => history.push("/auth/signin")}>
                  Retour à la connexion
                </Button>
              </Box>
            ) : (
              <Box>
                <Text fontSize="sm" color="gray.500" mb="24px" lineHeight="1.5">Choisissez un nouveau mot de passe pour votre compte.</Text>

                <Label>Nouveau mot de passe</Label>
                <InputGroup mb="18px">
                  <Input {...champStyle} type={showPwd ? "text" : "password"} placeholder="8 caractères minimum" value={password} onChange={(e) => setPassword(e.target.value)} />
                  <InputRightElement h="46px">
                    <IconButton aria-label="Afficher le mot de passe" variant="ghost" size="sm" color="gray.400" icon={showPwd ? <FaEyeSlash /> : <FaEye />} onClick={() => setShowPwd((v) => !v)} _hover={{ color: "teal.400", bg: "transparent" }} />
                  </InputRightElement>
                </InputGroup>

                <Label>Confirmer le mot de passe</Label>
                <Input {...champStyle} type={showPwd ? "text" : "password"} placeholder="Retapez le mot de passe" value={confirm} onChange={(e) => setConfirm(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") definir(); }} />

                {erreur && <Text color="red.400" fontSize="sm" mt="12px">{erreur}</Text>}

                <Button w="100%" mt="20px" h="46px" bg="teal.400" color="white" borderRadius="12px" fontWeight="bold" isLoading={chargement} _hover={{ bg: "teal.500" }} onClick={definir}>
                  Mettre à jour le mot de passe
                </Button>
              </Box>
            )}

            <Flex justify="center" mt="24px">
              <Text fontSize="sm" color="gray.500">
                <Link as={NavLink} to="/auth/signin" color="teal.500" fontWeight="bold">Retour à la connexion</Link>
              </Text>
            </Flex>
          </Box>

          <Flex flex="1" align="center" justify="center" minH={{ base: "180px", lg: "460px" }} position="relative" w="100%">
            <Image src={logoNotitia} alt="notitia" w={{ base: "70%", lg: "82%" }} maxW="460px" objectFit="contain" filter="drop-shadow(0 20px 34px rgba(45,116,110,0.25))" />
          </Flex>
        </Flex>
      </Box>
    </Flex>
  );
}
